import React from 'react';
import { api } from '../utils/api.js';

/**
 * Last line of defense. A throw during render — a storage read blocked by a
 * privacy profile, an undefined field on an unexpected API shape — otherwise
 * unmounts the whole tree and leaves the tech staring at a white screen in a
 * customer's kitchen with no way back.
 *
 * Wraps the app in main.jsx. Reload clears transient state; "Start fresh"
 * clears the saved scan, which is what a corrupted-state loop needs.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('Unhandled render error:', error, info && info.componentStack);
    // Logged to app_errors so a crash nobody reports still reaches triage
    // (D-LAUNCH4). Fire-and-forget: the crash screen must render regardless.
    try {
      api.reportClientError({
        message: String((error && error.message) || error).slice(0, 500),
        stack: String([error && error.stack, info && info.componentStack].filter(Boolean).join('\n--\n')).slice(0, 1500),
        route: window.location.pathname,
      }).catch(() => {});
    } catch {
      // Reporting is best-effort.
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleStartFresh = () => {
    try {
      window.localStorage.clear();
    } catch {
      // Storage is what broke us in the first place — reload anyway.
    }
    window.location.href = '/';
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-ink-900">
        <div className="card max-w-md w-full text-center">
          <div className="text-5xl mb-4" aria-hidden="true">🔧</div>
          <h1 className="text-2xl font-bold text-ink-50 mb-2">
            The app hit a snag
          </h1>
          <p className="text-ink-300 mb-6">
            Nothing you did caused this, and your saved scans are safe on the
            server. Reload to get going again.
          </p>
          <div className="flex flex-col gap-3">
            <button className="btn-primary w-full" onClick={this.handleReload}>
              Reload the app
            </button>
            <button className="btn-secondary w-full" onClick={this.handleStartFresh}>
              Start fresh
            </button>
          </div>
          <p className="text-ink-400 text-sm mt-6">
            Still stuck after both? Text Jason and mention what you were doing.
          </p>
        </div>
      </div>
    );
  }
}
